/**
 * History router. Matching, params and guards are pure and covered by tests.
 * `startRouter` is the browser half.
 */

import { sectionLabel } from './route-map.js';

export const ROUTES = [
  { path: '/app/dashboard', id: 'dashboard', title: 'Dashboard', crumb: 'Dashboard', guard: 'auth', nav: 'dashboard' },
  { path: '/app/chat/new', id: 'chat-new', title: 'New chat', crumb: 'New chat', guard: 'auth', nav: 'chat' },
  { path: '/app/chat/:id', id: 'chat', title: 'Chat', crumb: 'Chat', guard: 'auth', nav: 'chat' },
  { path: '/app/chat', id: 'chat', title: 'Chat', crumb: 'Chat', guard: 'auth', nav: 'chat' },
  { path: '/app/knowledge', id: 'knowledge', title: 'Knowledge base', crumb: 'Knowledge base', guard: 'auth', nav: 'knowledge' },
  { path: '/app/connectors', id: 'connectors', title: 'Connectors', crumb: 'Connectors', guard: 'auth', nav: 'connectors' },
  { path: '/app/schedules', id: 'schedules', title: 'Schedules', crumb: 'Schedules', guard: 'auth', nav: 'schedules' },
  { path: '/app/settings/models/:modelId', id: 'model-detail', title: 'AI model', crumb: 'AI models', guard: 'auth', nav: 'settings' },
  { path: '/app/settings/:section', id: 'settings', title: 'Settings', crumb: 'Settings', guard: 'auth', nav: 'settings' },
  { path: '/app/settings', id: 'settings', title: 'Settings', crumb: 'Settings', guard: 'auth', nav: 'settings' },
  // Legacy URL - resolveLocation rewrites to /app/settings/organizations
  { path: '/app/organizations', id: 'organizations-redirect', title: 'Organizations', crumb: 'Organizations', guard: 'auth', nav: 'settings' },
  { path: '/app/products/new', id: 'product-new', title: 'New product', crumb: 'New product · Setup', guard: 'auth', nav: '' },
  { path: '/app/products/:id', id: 'product-context', title: 'Product context', crumb: 'Product context', guard: 'auth', nav: 'settings' },
  { path: '/app/offline', id: 'offline', title: 'Offline', crumb: 'Offline', guard: null, nav: '' },
  { path: '/app/login', id: 'login', title: 'Sign in', crumb: 'Sign in', guard: null, nav: '' },
  { path: '/app/signup', id: 'signup', title: 'Sign up', crumb: 'Sign up', guard: null, nav: '' },
  { path: '/app/accept-invite', id: 'accept-invite', title: 'Accept invite', crumb: 'Accept invite', guard: null, nav: '' },
];

function decodePart(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function normalizePath(pathname) {
  const path = String(pathname || '').split('?')[0];
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path || '/';
}

/**
 * @param {string} pattern
 * @param {string} pathname
 * @returns {Record<string, string>|null}
 */
export function matchPattern(pattern, pathname) {
  const expected = normalizePath(pattern).split('/').filter(Boolean);
  const actual = normalizePath(pathname).split('/').filter(Boolean);
  if (expected.length !== actual.length) return null;
  const params = {};
  for (let i = 0; i < expected.length; i += 1) {
    if (expected[i].startsWith(':')) params[expected[i].slice(1)] = decodePart(actual[i]);
    else if (expected[i] !== actual[i]) return null;
  }
  return params;
}

/**
 * First matching route wins, so `/app/chat/new` stays ahead of `/app/chat/:id`.
 * @param {string} pathname
 * @param {typeof ROUTES} [routes]
 */
export function matchRoute(pathname, routes = ROUTES) {
  const path = normalizePath(pathname);
  for (const route of routes) {
    const params = matchPattern(route.path, path);
    if (params) return { route, params };
  }
  return null;
}

/**
 * @param {string|null|undefined} guard
 * @param {{ token?: string, role?: string, tenantSlug?: string }} [ctx]
 */
export function routeAllowed(guard, ctx) {
  const user = ctx || {};
  if (!guard) return true;
  if (guard === 'auth') return !!user.token;
  if (guard === 'super-admin') {
    return !!user.token && user.role === 'admin' && user.tenantSlug === 'craftisanlabs';
  }
  return false;
}

/**
 * @param {string} pathname
 * @param {string} [search]
 * @param {{ token?: string, role?: string, tenantSlug?: string }} ctx
 * @param {typeof ROUTES} [routes]
 */
export function resolveLocation(pathname, search, ctx, routes = ROUTES) {
  let path = normalizePath(pathname);
  if (path === '/app') path = '/app/dashboard';
  if (path === '/app/organizations') path = '/app/settings/organizations';
  const matched = matchRoute(path, routes);
  if (!matched) {
    return {
      id: 'not-found',
      title: 'Not found',
      crumb: 'Not found',
      nav: '',
      path,
      params: {},
      denied: false,
      missing: true,
      search: search || '',
    };
  }
  const params = matched.params;
  let crumb = matched.route.crumb;
  if (matched.route.id === 'settings' && params.section) crumb = sectionLabel(params.section);
  if (matched.route.id === 'model-detail') crumb = 'AI models · Detail';
  if (matched.route.id === 'product-context') crumb = 'Product context';
  return {
    ...matched.route,
    crumb,
    path,
    params,
    denied: !routeAllowed(matched.route.guard, ctx),
    missing: false,
    search: search || '',
  };
}

/** @param {string} path */
export function navigate(path) {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new Event('popstate'));
}

/**
 * @param {{ context: () => { token?: string, role?: string, tenantSlug?: string }, onRoute: (view: object) => void }} opts
 */
export function startRouter({ context, onRoute }) {
  function render() {
    const path = normalizePath(window.location.pathname);
    if (path === '/app/organizations') {
      const qs = window.location.search || '';
      window.history.replaceState(null, '', `/app/settings/organizations${qs}`);
    }
    const view = resolveLocation(window.location.pathname, window.location.search, context());
    if (view.denied && view.guard === 'auth') {
      window.location.replace('/app/login');
      return;
    }
    onRoute(view);
  }

  window.addEventListener('popstate', render);
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[href^="/app"]');
    if (!link || link.getAttribute('target') === '_blank') return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    event.preventDefault();
    navigate(`${url.pathname}${url.search}`);
  });
  render();
  return render;
}
